import * as React from 'react';
import { getThumbnailFilePathUsingFileName } from '@deltasierra/shared';
import { noop } from '@deltasierra/utilities-object';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { Loading } from '../Generic';
import './FileUpload.scss';

export type FileUploadModalProps = {
    dataCy?: string;
    processingFiles: File[];
    completedFiles: Array<{ file: File; thumbnail?: string }>;
    failedFiles: File[];
    onSelectFiles: (files: File[]) => void;
    onRemoveFile?: (file: File) => void;
};

const FileUpload: React.FunctionComponent<FileUploadModalProps> = ({
    completedFiles,
    dataCy,
    failedFiles,
    onRemoveFile = noop,
    onSelectFiles,
    processingFiles,
}) => {
    const fileUploadRef = React.useRef<HTMLInputElement>(null);
    const allFiles: Array<{ file: File; state: 'completed' | 'failed' | 'processing'; thumbnail?: string }> = [
        ...processingFiles.map(file => ({ file, state: 'processing' }) as const),
        ...completedFiles.map(({ file, thumbnail }) => ({ file, state: 'completed', thumbnail }) as const),
        ...failedFiles.map(file => ({ file, state: 'failed' }) as const),
    ];

    const handleOnFileInputChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        if (event.target.files) {
            onSelectFiles(Array.from(event.target.files));
        }

        if (fileUploadRef.current) {
            fileUploadRef.current.value = '';
        }
    };

    return (
        <div className="file-upload">
            <div className="row">
                <div className="form-group col-xs-12">
                    <input
                        className="form-control"
                        data-cy={dataCy}
                        multiple
                        ref={fileUploadRef}
                        type="file"
                        onChange={handleOnFileInputChange}
                    />
                </div>
            </div>
            <div className="row" style={{ maxHeight: 300, overflowY: 'auto' }}>
                <div className="col-xs-10 col-xs-offset-1">
                    <SelectedFilesList files={allFiles} onRemoveFile={onRemoveFile} />
                </div>
            </div>
        </div>
    );
};

FileUpload.displayName = 'FileUpload';

type SelectedFilesListProps = {
    files: Array<{ file: File; state: 'completed' | 'failed' | 'processing'; thumbnail?: string }>;
    onRemoveFile: (file: File) => void;
};

const SelectedFilesList: React.FunctionComponent<SelectedFilesListProps> = ({ files, onRemoveFile }) => (
    <>
        {files.length > 0 && (
            <div className="row file-upload__files-list">
                {files.map((file, index) => (
                    <div className="col-md-6" key={index}>
                        <div className="file-upload__files-list__file">
                            {file.state === 'processing' && (
                                <div className="file-upload__files-list__file__icon-wrapper--loading">
                                    <Loading inline size="small" />
                                </div>
                            )}
                            {file.state === 'completed' && (
                                <img
                                    alt={file.file.name}
                                    height={50}
                                    src={file.thumbnail ?? getThumbnailFilePathUsingFileName(file.file.name)}
                                    width={50}
                                />
                            )}
                            {file.state === 'failed' && (
                                <div className="file-upload__files-list__file__icon-wrapper--failed">
                                    <HighlightOffIcon style={{ fontSize: '28px' }} />
                                </div>
                            )}
                            <div
                                style={{
                                    overflow: 'hidden',
                                    paddingLeft: 15,
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {file.file.name}
                            </div>
                            <button
                                aria-label="Close"
                                className="close"
                                type="button"
                                onClick={() => onRemoveFile(file.file)}
                            >
                                {/* eslint-disable-next-line react/jsx-no-literals */}
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        )}
    </>
);

SelectedFilesList.displayName = 'FileUpload';

export default FileUpload;
